/**
 * Shared constants and helpers for detecting / counting images in markdown text.
 *
 * These patterns intentionally match sweagentd's patterns in
 * runtime/src/model/capi/image-utils.ts so that the frontend and backend
 * agree on what constitutes an image reference.
 */

/** Matches HTML img tags: `<img src="url">` */
export const IMG_TAG_PATTERN = /<img\b[^>]*\bsrc="([^"]+)"[^>]*>/g

/**
 * Extract all image URLs from text using both markdown and HTML img patterns.
 * Returns an array of {url, alt} objects for each match.
 */
export function extractImageUrls(text: string): Array<{url: string; alt: string}> {
  const results = extractMarkdownImageUrls(text)

  for (const match of text.matchAll(IMG_TAG_PATTERN)) {
    const [fullMatch, url] = match
    if (url) {
      const altMatch = fullMatch.match(/\balt="([^"]*)"/)
      results.push({url, alt: altMatch?.[1] || ''})
    }
  }

  return results
}

function extractMarkdownImageUrls(text: string): Array<{url: string; alt: string}> {
  const results: Array<{url: string; alt: string}> = []

  for (let index = 0; index < text.length - 1; index++) {
    if (text[index] !== '!' || text[index + 1] !== '[') continue

    const altStart = index + 2
    let altEnd = altStart
    while (altEnd < text.length && text[altEnd] !== ']') altEnd++

    if (text[altEnd + 1] !== '(') {
      index = altEnd
      continue
    }

    const urlStart = altEnd + 2
    let urlEnd = urlStart
    while (urlEnd < text.length && text[urlEnd] !== ')') urlEnd++

    if (urlEnd === text.length) break

    const url = text.slice(urlStart, urlEnd)
    if (url) results.push({url, alt: text.slice(altStart, altEnd)})
    index = urlEnd
  }

  return results
}
